import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Headphones, X } from "lucide-react";
import { useAudio } from "../context/AudioContext";

/* A whisper-quiet first-visit onboarding moment — never a pop-up or modal.
   A soft translucent prompt gently fades in after a few seconds, invites the
   visitor into the soundscape, and disappears the instant they interact.
   Shown only once, ever (remembered in localStorage). */
const CUE_KEY = "ttl-sound-cue";

export function SoundCue() {
  const { t } = useTranslation();
  const { toggle } = useAudio();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let seen = true;
    try { seen = localStorage.getItem(CUE_KEY) === "seen"; } catch (e) {}
    if (seen) return;
    const showTimer = setTimeout(() => {
      setVisible(true);
      try { localStorage.setItem(CUE_KEY, "seen"); } catch (e) {}
    }, 2600);
    // Never linger — gently retire if the visitor doesn't engage.
    const hideTimer = setTimeout(() => setVisible(false), 16000);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, []);

  if (!visible) return null;

  const enable = () => { toggle(); setVisible(false); };
  const dismiss = (e) => { e.stopPropagation(); setVisible(false); };

  return (
    <div
      className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] ttl-cue-in"
      data-testid="sound-cue"
    >
      <button
        onClick={enable}
        data-testid="sound-cue-enable"
        className="group inline-flex items-center gap-3 rounded-full border border-white/10 bg-black/45 backdrop-blur-2xl pl-4 pr-3 py-2.5 shadow-2xl transition-colors duration-500 hover:bg-black/60"
      >
        <Headphones className="w-3.5 h-3.5 text-[var(--accent-bronze)]" strokeWidth={1.5} />
        <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-white/80">
          {t("soundCue.prompt")}
        </span>
        <span
          onClick={dismiss}
          role="button"
          tabIndex={0}
          aria-label={t("soundCue.dismiss")}
          data-testid="sound-cue-dismiss"
          className="ml-1 inline-flex items-center justify-center rounded-full p-1 text-white/40 hover:text-white/80 transition-colors duration-300"
        >
          <X className="w-3 h-3" strokeWidth={1.5} />
        </span>
      </button>
    </div>
  );
}

export default SoundCue;
